// Meu Radar — a agenda pessoal (21/09/2026 como radar de editais; pessoal
// desde 22/09/2026, a pedido do Gustavo).
//
// Um calendário e uma agenda com o que EU escolhi acompanhar: editais,
// negócios do pipeline (com o tipo de proposta a produzir) e lembretes livres
// (CREA, marketing, contato com cliente, reunião). Cada item avisa por e-mail
// N dias antes do prazo.
//
// ── Quatro decisões de tela ──────────────────────────────────────────────
//
// 1. 🚨 O calendário marca DOIS dias por item: ⏰ o dia em que o aviso sai e
//    🏁 o prazo. Só esses dois, e ambos vêm do servidor. Datas que o modelo LEU
//    no edital (visita técnica) aparecem como TEXTO nos aspectos, nunca como
//    dia de calendário: data brasileira não sobrevive ao modelo (o Haiku lê
//    12/11 como 11 de dezembro). Um dia errado no calendário é pior que nenhum.
//
// 2. A cor e o "em N dias" saem de `classificarPrazoRadar` (api.ts), que
//    conta DIAS de calendário (o `dias_restantes` do servidor) com as mesmas
//    faixas de cor da coluna Prazo da lista de oportunidades. 🚨 (25/09/2026)
//    Não volte para o `classificarPrazo` do prazo.ts aqui: ele conta
//    milissegundos e dizia "amanhã" no próprio dia do prazo, "🔥 HOJE" depois
//    de encerrado e "em 8 dias" para o +7 — o contrário do e-mail.
//
// 3. Edital NÃO entra por aqui: entra pelo 📡 da aba Oportunidades, que é onde
//    a pessoa está quando decide. Um seletor de edital nesta tela seria uma
//    segunda busca de licitação para manter — a tela diz onde ir.
//
// 4. Em telas estreitas o calendário some e fica só a agenda: o `Calendar`
//    mensal do antd não cabe em 375 px, e no celular o que importa é "o que
//    vence primeiro".
//
// ── Comercial › Meu Radar (24/09/2026) ───────────────────────────────────
//
// A tela saiu do Administrativo e virou a primeira aba do hub
// `ComercialRadarHub` (/comercial/radar), com Atividades ao lado. /meu-radar
// redireciona preservando a query. Três mudanças de comportamento, pedidas
// pelo Gustavo — a visualização aprovada NÃO mudou:
//
// 5. Arrastar o 🏁 de negócio e de lembrete para outro dia muda o prazo
//    (`mover_para_dia`; o servidor mantém a hora). O 🏁 de edital não arrasta:
//    o prazo dele vem da FONTE (`prazoEfetivo()`), e arrastar criaria um
//    prazo_manual que deixa de acompanhar a prorrogação. O ⏰ também não: o dia
//    do aviso é consequência do prazo e da antecedência.
// 6. Negócio sem data de entrega ganha prazo automático de 7 dias, com aviso
//    no 7º dia às 8h (quem calcula é o servidor).
// 7. 🚨 Fuso (24/09/2026): `prazo`, `prazo_manual`, `alerta_em` e
//    `alerta_enviado_para_prazo` chegam como HORA DE PAREDE de Brasília, em
//    texto sem Z ('YYYY-MM-DDTHH:MM:SS'). `dayjs(s)`/`new Date(s)` leem esse
//    formato como hora local, que é o certo aqui. Antes o node-pg rotulava a
//    parede BRT como UTC ('…Z'): a tela mostrava 3 h antes, e o "Salvar" do
//    ModalEditar sem mexer em nada reenviava a hora recuada — o prazo andava
//    3 h para trás a cada clique. Nunca use `dayjs.utc`/`toISOString()` nesses
//    campos.
import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Card, Button, Typography, Space, Tag, Tooltip, Empty, Flex, Spin, Modal,
  Input, InputNumber, DatePicker, Form, Calendar, Badge, Alert, Popconfirm,
  Collapse, Descriptions, App as AntApp, Segmented, Divider, Select, Dropdown,
} from 'antd';
import {
  ReloadOutlined, MailOutlined, DeleteOutlined, EditOutlined, PlusOutlined,
  CalendarOutlined, UnorderedListOutlined, RobotOutlined, LinkOutlined,
  ExperimentOutlined, SearchOutlined,
} from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Se } from '../../components/Se';
import { usePerms } from '../../hooks/usePerms';
import { useIsMobile } from '../../hooks/useResponsive';
import { fmtDataHoraCurta, descreverOrigemPrazo } from '../comercial-licitacoes/prazo';
import {
  radarApi, ItemRadar, ResultadoAlertas, NegocioBusca, TipoItem, TipoProposta,
  Categoria, CATEGORIAS, ROTULO_TIPO, ROTULO_TIPO_PROPOSTA,
  DIAS_ANTECEDENCIA_PADRAO, estadoDoAviso, marcaDoItem, podeArrastarPrazo,
  FORMATO_PAREDE, classificarPrazoRadar, resumoDoNegocioNoRadar, PrazoRadar,
} from './api';

const { Title, Text, Paragraph } = Typography;

function fmtDinheiro(v: number | string | null): string {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `R$ ${n.toLocaleString('pt-BR', { minimumFractionDigits: 0 })}`;
}

function erroDaApi(e: any, padrao: string): string {
  return e?.response?.data?.erro || e?.response?.data?.error || e?.message || padrao;
}

// ═══════════════════════════════════════════════════════════════════════════
// Aspectos relevantes da IA — só existe para edital
// ═══════════════════════════════════════════════════════════════════════════
function AspectosDaIa({ item }: { item: ItemRadar }) {
  if (item.tipo !== 'licitacao') return null;
  const a = item.aspectos_ia;
  if (!a) {
    return (
      <Alert
        type="info" showIcon
        message="Este edital ainda não tem análise registrada."
        description={
          <Text style={{ fontSize: 12 }}>
            Abra a oportunidade em <strong>Comercial › Licitações</strong> e peça a análise.
            Assim que ela existir, os aspectos aparecem aqui e entram no e-mail do aviso.
          </Text>
        }
      />
    );
  }
  return (
    <div>
      <Flex align="center" gap={8} style={{ marginBottom: 8 }} wrap="wrap">
        <RobotOutlined style={{ color: '#020D7F' }} />
        <Text strong style={{ color: '#020D7F', fontSize: 13 }}>Aspectos relevantes da análise</Text>
        {a.fit_score != null && <Tag color="blue">fit {a.fit_score}/100</Tag>}
        <Text type="secondary" style={{ fontSize: 11 }}>análise de {fmtDataHoraCurta(a.criado_em)}</Text>
      </Flex>
      {a.resumo && (
        <Paragraph style={{ fontSize: 12, marginBottom: 10, whiteSpace: 'pre-wrap' }}>{a.resumo}</Paragraph>
      )}
      {a.itens.length > 0 && (
        <Descriptions
          size="small" column={1} bordered
          styles={{ label: { width: 190, fontSize: 11 }, content: { fontSize: 12 } }}
          items={a.itens.map((i, idx) => ({ key: String(idx), label: i.rotulo, children: i.texto }))}
        />
      )}
      <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 8 }}>
        Leitura automática do edital — confira sempre no documento original.
      </Text>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Modal: criar lembrete livre / acompanhar negócio
// ═══════════════════════════════════════════════════════════════════════════
// 🚨 Largura em vw com teto e corpo rolando só o que passa da tela — a
// preferência explícita do dono (CLAUDE.md, 2026-08-07).
function ModalNovo({
  tipo, aberto, onFechar, onCriado,
}: {
  tipo: 'negocio' | 'livre' | null;
  aberto: boolean;
  onFechar: () => void;
  onCriado: () => void;
}) {
  const { message } = AntApp.useApp();
  const [form] = Form.useForm();
  const [salvando, setSalvando] = useState(false);
  const [negocios, setNegocios] = useState<NegocioBusca[]>([]);
  const [buscando, setBuscando] = useState(false);
  // Negócio sem data (24/09/2026): o prazo vira automático no servidor e a
  // antecedência some do formulário — ver `salvar`.
  const prazoEscolhido = Form.useWatch('prazo_manual', form) as Dayjs | null | undefined;
  const negocioSemData = tipo === 'negocio' && !prazoEscolhido;

  useEffect(() => {
    if (!aberto) return;
    form.resetFields();
    form.setFieldsValue({ dias_antecedencia: DIAS_ANTECEDENCIA_PADRAO, categoria: 'crea' as Categoria });
    if (tipo === 'negocio') {
      setBuscando(true);
      radarApi.buscarNegocios('')
        .then(setNegocios)
        .catch(() => setNegocios([]))
        .finally(() => setBuscando(false));
    }
  }, [aberto, tipo, form]);

  if (!tipo) return null;

  const buscarNegocios = async (termo: string) => {
    setBuscando(true);
    try { setNegocios(await radarApi.buscarNegocios(termo)); }
    catch { /* a busca vazia não quebra o formulário */ }
    finally { setBuscando(false); }
  };

  const salvar = async () => {
    const v = await form.validateFields();
    setSalvando(true);
    try {
      // 🚨 Negócio sem data (24/09/2026): NÃO manda `prazo_manual` nem
      // `dias_antecedencia`. O servidor põe o prazo em hoje + 7 dias às 18h
      // (em Brasília) com antecedência 0 — aviso no 7º dia às 8h. Mandar o 5
      // que o formulário guarda por baixo faria o aviso sair no 2º dia, o
      // contrário do que o texto de ajuda promete.
      const semData = tipo === 'negocio' && !v.prazo_manual;
      const comum = {
        ...(semData ? {} : {
          prazo_manual: (v.prazo_manual as Dayjs).format(FORMATO_PAREDE),
          dias_antecedencia: v.dias_antecedencia,
        }),
        alerta_email: v.alerta_email || undefined,
        observacoes: v.observacoes ?? '',
      };
      if (tipo === 'negocio') {
        const novo = await radarApi.acompanharNegocio(v.negocio_id, { ...comum, tipo_proposta: v.tipo_proposta });
        // 🚨 (25/09/2026) A frase sai do item que o SERVIDOR devolveu (prazo,
        // antecedência e estado do aviso reais), não da promessa do
        // formulário: readicionar um negócio ativo mantém a data combinada.
        const resumo = resumoDoNegocioNoRadar(novo);
        if (resumo.encerrado) message.warning(resumo.texto);
        else message.success(resumo.texto);
      } else {
        await radarApi.criarLembrete({ ...comum, titulo: v.titulo, categoria: v.categoria });
        message.success('Lembrete criado.');
      }
      onCriado();
      onFechar();
    } catch (e: any) {
      message.error(erroDaApi(e, 'Falha ao salvar.'));
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Modal
      open={aberto}
      onCancel={onFechar}
      title={tipo === 'negocio' ? '💼 Acompanhar negócio do pipeline' : '📌 Novo lembrete'}
      width="94vw"
      style={{ maxWidth: 720, top: 24 }}
      styles={{ body: { maxHeight: 'calc(100vh - 220px)', overflowY: 'auto' } }}
      okText="Colocar no radar"
      cancelText="Cancelar"
      confirmLoading={salvando}
      onOk={salvar}
    >
      <Form form={form} layout="vertical" size="small">
        {tipo === 'negocio' ? (
          <>
            <Form.Item
              label="Qual negócio" name="negocio_id"
              rules={[{ required: true, message: 'Escolha o negócio' }]}
            >
              <Select
                showSearch filterOption={false} loading={buscando}
                onSearch={buscarNegocios}
                placeholder="Digite o título ou o cliente"
                suffixIcon={<SearchOutlined />}
                options={negocios.map(n => ({
                  value: n.id,
                  label: `${n.titulo}${n.cliente ? ` · ${n.cliente}` : ''}${n.status ? ` · ${n.status}` : ''}`,
                }))}
                notFoundContent={buscando ? <Spin size="small" /> : 'Nenhum negócio aberto encontrado'}
              />
            </Form.Item>

            {/* O pedido: "que pergunte o tipo de proposta que deve ser feito". */}
            <Form.Item
              label="O que precisa ser produzido" name="tipo_proposta"
              rules={[{ required: true, message: 'Escolha o que precisa ser feito' }]}
              extra={
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Isto é planejamento do seu radar — não muda o documento que o sistema gera.
                </Text>
              }
            >
              <Select
                options={(Object.keys(ROTULO_TIPO_PROPOSTA) as TipoProposta[])
                  .map(k => ({ value: k, label: ROTULO_TIPO_PROPOSTA[k] }))}
                placeholder="Somente técnica, ou técnica e preço"
              />
            </Form.Item>
          </>
        ) : (
          <>
            <Form.Item
              label="Do que você quer ser lembrado" name="titulo"
              rules={[{ required: true, message: 'Escreva o lembrete' }]}
            >
              <Input placeholder="Renovar o atestado do CREA · Ligar para o cliente X · Post da campanha" />
            </Form.Item>
            <Form.Item label="Categoria" name="categoria">
              <Select
                options={CATEGORIAS.map(c => ({ value: c.valor, label: `${c.emoji}  ${c.rotulo}` }))}
              />
            </Form.Item>
          </>
        )}

        <Flex gap={12} wrap="wrap">
          <Form.Item
            label={tipo === 'negocio' ? 'Entrega da proposta' : 'Quando'}
            name="prazo_manual"
            style={{ flex: '1 1 240px', minWidth: 220 }}
            // Negócio: opcional desde 24/09/2026 (sem data = 7 dias). Lembrete
            // continua obrigatório — "me lembre" sem quando não é lembrete.
            rules={tipo === 'negocio' ? [] : [{ required: true, message: 'Informe a data' }]}
            extra={tipo === 'negocio' ? (
              <Text type="secondary" style={{ fontSize: 11 }}>
                Sem data: 7 dias a partir de hoje, com aviso no 7º dia às 8h
              </Text>
            ) : undefined}
          >
            <DatePicker showTime format="DD/MM/YYYY HH:mm" style={{ width: '100%' }} />
          </Form.Item>

          {/* Sem data, a antecedência é a do prazo automático (0 = no dia) e
              não vai para o servidor: mostrar o campo seria prometer um aviso
              que não é o que sai. */}
          {!negocioSemData && (
            <Form.Item
              label="Avisar com antecedência de" name="dias_antecedencia"
              style={{ flex: '1 1 180px', minWidth: 170 }}
              rules={[{ required: true, message: 'Informe a antecedência' }]}
              extra={<Text type="secondary" style={{ fontSize: 11 }}>O aviso sai às 8h do dia.</Text>}
            >
              <InputNumber min={0} max={90} precision={0} style={{ width: '100%' }} addonAfter="dias" />
            </Form.Item>
          )}
        </Flex>

        <Form.Item
          label="Enviar o aviso para" name="alerta_email"
          extra={<Text type="secondary" style={{ fontSize: 11 }}>Em branco, vai para o seu e-mail.</Text>}
        >
          <Input prefix={<MailOutlined />} placeholder="seu e-mail" />
        </Form.Item>

        <Form.Item label="Observações e anotações" name="observacoes">
          <Input.TextArea rows={4} placeholder="O que já foi analisado, o que preocupa, decisões…" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Modal de edição
// ═══════════════════════════════════════════════════════════════════════════
function ModalEditar({
  item, aberto, onFechar, onSalvo,
}: {
  item: ItemRadar | null;
  aberto: boolean;
  onFechar: () => void;
  onSalvo: () => void;
}) {
  const { message } = AntApp.useApp();
  const [form] = Form.useForm();
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!aberto || !item) return;
    form.setFieldsValue({
      dias_antecedencia: item.dias_antecedencia,
      alerta_email: item.alerta_email,
      // Parede BRT sem Z → dayjs lê como hora local, a mesma que a tela mostra.
      prazo_manual: item.prazo_manual ? dayjs(item.prazo_manual) : null,
      observacoes: item.observacoes || '',
      titulo: item.titulo || '',
      categoria: item.categoria || 'outro',
      tipo_proposta: item.tipo_proposta || undefined,
    });
  }, [aberto, item, form]);

  if (!item) return null;

  const prazoCls = classificarPrazoRadar(item);
  const segueAFonte = item.tipo === 'licitacao';

  const salvar = async () => {
    const v = await form.validateFields();
    setSalvando(true);
    try {
      await radarApi.atualizar(item.id, {
        dias_antecedencia: v.dias_antecedencia,
        alerta_email: v.alerta_email,
        // String vazia = "volte a seguir o prazo da fonte" (só edital aceita).
        // 🚨 24/09/2026: reenvia EXATAMENTE a hora exibida, em parede BRT sem
        // offset. Com o '…Z' antigo, salvar sem mexer recuava o prazo 3 h.
        prazo_manual: v.prazo_manual ? (v.prazo_manual as Dayjs).format(FORMATO_PAREDE) : '',
        observacoes: v.observacoes ?? '',
        ...(item.tipo === 'livre' ? { titulo: v.titulo, categoria: v.categoria } : {}),
        ...(item.tipo === 'negocio' ? { tipo_proposta: v.tipo_proposta ?? null } : {}),
      });
      message.success('Radar atualizado.');
      onSalvo();
      onFechar();
    } catch (e: any) {
      message.error(erroDaApi(e, 'Falha ao salvar.'));
    } finally {
      setSalvando(false);
    }
  };

  const marca = marcaDoItem(item);

  return (
    <Modal
      open={aberto}
      onCancel={onFechar}
      title={<span>{marca.emoji} {item.rotulo}</span>}
      width="94vw"
      style={{ maxWidth: 820, top: 16 }}
      styles={{ body: { maxHeight: 'calc(100vh - 190px)', overflowY: 'auto' } }}
      okText="Salvar"
      cancelText="Fechar"
      confirmLoading={salvando}
      onOk={salvar}
    >
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
        {ROTULO_TIPO[item.tipo]}
        {item.tipo === 'licitacao' && item.orgao ? ` · ${item.orgao}` : ''}
        {item.tipo === 'negocio' && item.negocio_cliente ? ` · ${item.negocio_cliente}` : ''}
      </Text>

      {prazoCls && (
        <Alert
          type={prazoCls.dias <= 3 ? 'error' : prazoCls.dias <= 7 ? 'warning' : 'info'}
          showIcon style={{ marginBottom: 14 }}
          message={<span>Prazo <strong>{fmtDataHoraCurta(item.prazo)}</strong> — {prazoCls.rotulo}</span>}
          description={
            <Text type="secondary" style={{ fontSize: 11 }}>
              {item.prazo_manual
                ? 'Data informada à mão nesta tela.'
                : descreverOrigemPrazo(item.origem_prazo)}
            </Text>
          }
        />
      )}

      <Form form={form} layout="vertical" size="small">
        {item.tipo === 'livre' && (
          <Flex gap={12} wrap="wrap">
            <Form.Item label="Lembrete" name="titulo" style={{ flex: '2 1 300px' }}
              rules={[{ required: true, message: 'Escreva o lembrete' }]}>
              <Input />
            </Form.Item>
            <Form.Item label="Categoria" name="categoria" style={{ flex: '1 1 200px' }}>
              <Select options={CATEGORIAS.map(c => ({ value: c.valor, label: `${c.emoji}  ${c.rotulo}` }))} />
            </Form.Item>
          </Flex>
        )}

        {item.tipo === 'negocio' && (
          <Form.Item
            label="O que precisa ser produzido" name="tipo_proposta"
            extra={<Text type="secondary" style={{ fontSize: 11 }}>
              Planejamento do seu radar — não muda o documento que o sistema gera.
            </Text>}
          >
            <Select allowClear
              options={(Object.keys(ROTULO_TIPO_PROPOSTA) as TipoProposta[])
                .map(k => ({ value: k, label: ROTULO_TIPO_PROPOSTA[k] }))} />
          </Form.Item>
        )}

        <Flex gap={12} wrap="wrap">
          <Form.Item
            label="Avisar com quantos dias de antecedência" name="dias_antecedencia"
            style={{ flex: '1 1 220px', minWidth: 200 }}
            rules={[{ required: true, message: 'Informe a antecedência' }]}
            extra={<Text type="secondary" style={{ fontSize: 11 }}>
              Padrão da casa: {DIAS_ANTECEDENCIA_PADRAO} dias. O aviso sai às 8h do dia; 0 = no dia do prazo.
            </Text>}
          >
            <InputNumber min={0} max={90} precision={0} style={{ width: '100%' }} addonAfter="dias" />
          </Form.Item>

          <Form.Item
            label="Enviar o aviso para" name="alerta_email"
            style={{ flex: '2 1 300px', minWidth: 240 }}
            rules={[{ required: true, message: 'Informe ao menos um e-mail' }]}
            extra={<Text type="secondary" style={{ fontSize: 11 }}>Mais de um: separe por vírgula.</Text>}
          >
            <Input prefix={<MailOutlined />} />
          </Form.Item>
        </Flex>

        <Form.Item
          label={segueAFonte ? 'Prazo (só se a fonte estiver errada ou vazia)' : 'Prazo'}
          name="prazo_manual"
          rules={segueAFonte ? [] : [{ required: true, message: 'Informe a data' }]}
          extra={
            <Text type="secondary" style={{ fontSize: 11 }}>
              {segueAFonte
                ? 'Em branco, segue o prazo da fonte — e se o órgão prorrogar, a data e o aviso se ajustam sozinhos.'
                : 'Este item não tem prazo na fonte; a data é sua.'}
            </Text>
          }
        >
          <DatePicker showTime format="DD/MM/YYYY HH:mm" style={{ width: '100%', maxWidth: 260 }}
            allowClear={segueAFonte} />
        </Form.Item>

        <Form.Item label="Observações e anotações" name="observacoes"
          extra={<Text type="secondary" style={{ fontSize: 11 }}>Vai junto no e-mail do aviso.</Text>}>
          <Input.TextArea rows={6} />
        </Form.Item>
      </Form>

      {item.tipo === 'licitacao' && (
        <>
          <Divider style={{ margin: '4px 0 12px' }} />
          <AspectosDaIa item={item} />
        </>
      )}
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Cartão de um item
// ═══════════════════════════════════════════════════════════════════════════
function CartaoItem({
  item, podeGerenciar, onEditar, onRemover,
}: {
  item: ItemRadar;
  podeGerenciar: boolean;
  onEditar: (i: ItemRadar) => void;
  onRemover: (i: ItemRadar) => void;
}) {
  const prazo = classificarPrazoRadar(item);
  const estado = estadoDoAviso(item);
  const marca = marcaDoItem(item);

  const subtitulo = item.tipo === 'licitacao'
    ? [item.orgao, item.uf ? `${item.cidade || ''}/${item.uf}` : null, item.modalidade]
        .filter(Boolean).join(' · ')
    : item.tipo === 'negocio'
      ? [item.negocio_cliente, item.negocio_status, item.negocio_responsavel]
          .filter(Boolean).join(' · ')
      : marca.rotulo;

  return (
    <Card size="small" style={{ marginBottom: 10, borderLeft: `4px solid ${prazo?.cor || '#d9d9d9'}` }}
      styles={{ body: { padding: 12 } }}>
      <Flex justify="space-between" align="flex-start" gap={12} wrap="wrap">
        <div style={{ flex: '1 1 320px', minWidth: 260 }}>
          <Flex align="center" gap={8} wrap="wrap">
            <Text strong style={{ fontSize: 13, color: '#020D7F' }}>
              {marca.emoji} {item.rotulo}
            </Text>
            <Tag style={{ fontSize: 10 }}>{ROTULO_TIPO[item.tipo]}</Tag>
            {item.tipo === 'negocio' && item.tipo_proposta && (
              <Tag color="purple" style={{ fontSize: 10 }}>
                {ROTULO_TIPO_PROPOSTA[item.tipo_proposta]}
              </Tag>
            )}
            {item.tipo === 'licitacao' && item.fit_score != null && (
              <Tag style={{ fontSize: 10 }}>fit {item.fit_score}</Tag>
            )}
            {item.link_edital && (
              <a href={item.link_edital} target="_blank" rel="noreferrer" style={{ fontSize: 11 }}>
                <LinkOutlined /> edital
              </a>
            )}
          </Flex>
          <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
            {subtitulo}
            {item.tipo === 'licitacao' && item.valor_estimado != null ? ` · ${fmtDinheiro(item.valor_estimado)}` : ''}
            {item.tipo === 'negocio' && item.negocio_valor != null ? ` · ${fmtDinheiro(item.negocio_valor)}` : ''}
          </Text>
          {item.objeto && (
            <Text style={{ fontSize: 12, display: 'block', marginTop: 4 }} ellipsis={{ tooltip: item.objeto }}>
              {item.objeto}
            </Text>
          )}
        </div>

        <div style={{ flex: '0 0 auto', textAlign: 'right', minWidth: 190 }}>
          {prazo ? (
            <>
              <div style={{ fontSize: 13, fontWeight: prazo.bold ? 600 : 400, color: prazo.cor }}>
                🏁 {prazo.icone}{prazo.rotulo}
              </div>
              <Tooltip title={item.prazo_manual ? 'Data informada à mão' : descreverOrigemPrazo(item.origem_prazo)}>
                <div style={{ fontSize: 11, color: '#888' }}>{fmtDataHoraCurta(item.prazo)}</div>
              </Tooltip>
            </>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>sem prazo</Text>
          )}
          {/* O dia e a hora do aviso já vêm dentro de `estado.texto` ("aviso em
              29/09 às 8h") — a regra por dia de 24/09/2026 mora em estadoDoAviso. */}
          <div style={{ fontSize: 11, color: estado.cor, marginTop: 4 }}>
            ⏰ {estado.texto}
          </div>
          <Tooltip title={item.dias_antecedencia
            ? `Aviso às 8h, ${item.dias_antecedencia} dia(s) antes do prazo, para ${item.alerta_email}`
            : `Aviso às 8h do dia do prazo, para ${item.alerta_email}`}>
            <div style={{ fontSize: 11, color: '#888' }}><MailOutlined /> {item.alerta_email}</div>
          </Tooltip>
        </div>
      </Flex>

      {item.observacoes && (
        <div style={{ marginTop: 10, padding: '8px 10px', background: '#F6F7FB',
          borderRadius: 4, fontSize: 12, whiteSpace: 'pre-wrap' }}>
          <Text type="secondary" style={{ fontSize: 10, display: 'block', marginBottom: 2 }}>
            Suas anotações
          </Text>
          {item.observacoes}
        </div>
      )}

      {item.tipo === 'licitacao' && (
        <Collapse ghost size="small" style={{ marginTop: 4 }}
          items={[{
            key: 'ia',
            label: (
              <Text style={{ fontSize: 12 }}>
                <RobotOutlined /> Aspectos relevantes da análise
                {item.aspectos_ia ? ` (${item.aspectos_ia.itens.length})` : ' — sem análise ainda'}
              </Text>
            ),
            children: <AspectosDaIa item={item} />,
          }]}
        />
      )}

      {podeGerenciar && (
        <Flex gap={8} justify="flex-end" style={{ marginTop: 6 }}>
          <Button size="small" icon={<EditOutlined />} onClick={() => onEditar(item)}>Editar</Button>
          <Popconfirm
            title="Tirar do radar?"
            description={<div style={{ fontSize: 11, maxWidth: 230 }}>
              Sai do calendário e para de avisar. As anotações ficam guardadas.
            </div>}
            okText="Tirar" cancelText="Cancelar"
            okButtonProps={{ danger: true, size: 'small' }}
            onConfirm={() => onRemover(item)}
          >
            <Button size="small" danger type="text" icon={<DeleteOutlined />} />
          </Popconfirm>
        </Flex>
      )}
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Página
// ═══════════════════════════════════════════════════════════════════════════
export default function MeuRadar() {
  return (
    <AntApp>
      <MeuRadarInner />
    </AntApp>
  );
}

type Filtro = 'todos' | TipoItem;

function MeuRadarInner() {
  const { message, modal } = AntApp.useApp();
  const { pode } = usePerms();
  const mobile = useIsMobile();
  // Sem atalho de admin: o `checarPerm` do servidor não tem bypass, então
  // liberar o botão por ser admin mostraria "Editar" para quem leva 403.
  const podeGerenciar = pode('radar.meu.gerenciar');
  const podeVerPipeline = pode('comercial.pipeline.ver');
  const podeVerOportunidades = pode('comercial.licitacoes.oportunidades.ver');

  const [itens, setItens] = useState<ItemRadar[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [vista, setVista] = useState<'calendario' | 'lista'>('calendario');
  const [filtro, setFiltro] = useState<Filtro>('todos');
  const [editando, setEditando] = useState<ItemRadar | null>(null);
  const [criando, setCriando] = useState<'negocio' | 'livre' | null>(null);
  const [verificando, setVerificando] = useState(false);
  // Calendário CONTROLADO (24/09/2026): o mês visível mora aqui, e não dentro
  // do `Calendar`. Sem isso, cada "Atualizar" (e cada soltar) desmontava a
  // grade e ela voltava para o mês corrente — quem arrastava um prazo de
  // novembro era jogado de volta para setembro.
  const [mesVisivel, setMesVisivel] = useState<Dayjs>(() => dayjs());
  // Arrastar: id do item em curso e dia sob o cursor (para o realce).
  const [arrastando, setArrastando] = useState<number | null>(null);
  const [diaAlvo, setDiaAlvo] = useState<string | null>(null);
  // `jaCarregou` = houve uma tentativa (tira o esqueleto da primeira carga);
  // `carregouOk` = houve uma carga BEM-SUCEDIDA. O deep-link espera a segunda:
  // com o radar_listar falhando, a lista vazia fazia o link ser consumido e o
  // aviso dizer "não está no seu radar" (25/09/2026).
  const [jaCarregou, setJaCarregou] = useState(false);
  const [carregouOk, setCarregouOk] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try { setItens(await radarApi.listar()); setCarregouOk(true); }
    catch (e: any) { message.error(erroDaApi(e, 'Falha ao carregar o radar.')); }
    finally { setCarregando(false); setJaCarregou(true); }
  }, [message]);

  useEffect(() => { carregar(); }, [carregar]);

  // ── Deep-link: abrir UM item ao chegar de fora (24/09/2026) ─────────────
  // Dois caminhos, duas fontes — o mesmo conserto de AbaOportunidades.tsx
  // (PR #288): o e-mail do aviso manda `?item=<id>` na URL, e o sino grava o
  // id em sessionStorage ('radar_abrir_item_id', pela ponte do App.tsx, que
  // navega sem query). 🚨 Até aqui o App gravava a chave e NINGUÉM lia: o
  // sino do lembrete largava a pessoa no calendário, procurando o item.
  //
  // O efeito roda a cada navegação (`location.key`): quem já está nesta aba e
  // clica noutro sino também abre o item (a aba não é remontada). As duas
  // fontes são CONSUMIDAS na leitura — a chave é apagada e o `?item=` sai da
  // URL com replace, preservando o resto (o `?aba=` do HubAbas); senão trocar
  // para Atividades e voltar reabria o modal a cada ida e volta.
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    // Só depois de uma carga que deu certo: se falhou, o `?item=` e a chave do
    // sino ficam guardados e o item abre no próximo "Atualizar" que funcionar.
    if (!carregouOk || carregando) return;
    let alvo: number | null = null;
    try {
      const sp = new URLSearchParams(location.search);
      const daUrl = sp.get('item');
      const doSino = sessionStorage.getItem('radar_abrir_item_id');
      if (doSino) sessionStorage.removeItem('radar_abrir_item_id');
      // A URL ganha: é o clique que a pessoa acabou de dar (a chave do sino
      // pode ter sobrado de uma navegação que saiu antes de a lista carregar).
      alvo = Number(daUrl || doSino) || null;
      if (daUrl) {
        sp.delete('item');
        const resto = sp.toString();
        navigate({ pathname: location.pathname, search: resto ? `?${resto}` : '' }, { replace: true });
      }
    } catch { /* sessionStorage bloqueado: o deep-link é conveniência, não requisito */ }
    if (!alvo) return;
    const achado = itens.find(i => i.id === alvo);
    if (achado) {
      setEditando(achado);
      // Leva a grade ao mês do prazo, para o item aparecer atrás do modal.
      if (achado.prazo) setMesVisivel(dayjs(achado.prazo));
    } else {
      // O radar é pessoal: item de outra pessoa ou tirado do radar não vem na
      // lista — e não há "detalhe" para buscar, o servidor filtra por dono.
      // Também chega aqui quem recebeu o aviso como co-destinatário: o link
      // leva o id do item do DONO.
      message.warning('Esse item não está no seu radar — ele pode ser de outra pessoa ou ter sido tirado dele.');
    }
  }, [location.key, location.search, location.pathname, carregouOk, carregando, itens, message, navigate]);

  const remover = async (i: ItemRadar) => {
    try {
      await radarApi.remover(i.id);
      message.success('Fora do radar.');
      carregar();
    } catch (e: any) { message.error(erroDaApi(e, 'Falha ao remover.')); }
  };

  // "Conferir avisos": SIMULA primeiro e só envia depois do OK — mesmo
  // contrato do "Arquivar vencidas". Botão que dispara e-mail direto é botão
  // que dispara e-mail por engano.
  const conferirAvisos = async () => {
    setVerificando(true);
    try {
      const previa = await radarApi.verificarAlertas(false);
      if (!previa.candidatos) {
        modal.info({
          title: 'Nenhum aviso para enviar agora',
          content: <Text style={{ fontSize: 13 }}>
            Nada entrou na janela de antecedência ainda — ou o aviso do prazo atual já saiu.
          </Text>,
        });
        return;
      }
      modal.confirm({
        title: `Enviar ${previa.candidatos} aviso(s)?`,
        width: 640,
        content: <ListaPrevia previa={previa} />,
        okText: 'Enviar agora',
        cancelText: 'Cancelar',
        onOk: async () => {
          const r = await radarApi.verificarAlertas(true);
          if (r.sucesso) message.success(`${r.enviados} aviso(s) enviado(s).`);
          else message.error(`${r.enviados} enviado(s), ${r.erros} falharam — os que falharam continuam armados.`);
          carregar();
        },
      });
    } catch (e: any) {
      message.error(erroDaApi(e, 'Falha ao conferir os avisos.'));
    } finally {
      setVerificando(false);
    }
  };

  const visiveis = useMemo(
    () => (filtro === 'todos' ? itens : itens.filter(i => i.tipo === filtro)),
    [itens, filtro]
  );

  // Marcações do calendário: ⏰ dia do aviso e 🏁 prazo.
  const porDia = useMemo(() => {
    const mapa = new Map<string, Array<{ item: ItemRadar; tipo: 'alerta' | 'prazo' }>>();
    const por = (iso: string | null, item: ItemRadar, tipo: 'alerta' | 'prazo') => {
      if (!iso) return;
      const d = dayjs(iso);
      if (!d.isValid()) return;
      const ymd = d.format('YYYY-MM-DD');
      if (!mapa.has(ymd)) mapa.set(ymd, []);
      mapa.get(ymd)!.push({ item, tipo });
    };
    for (const i of visiveis) {
      por(i.prazo, i, 'prazo');
      por(i.alerta_em, i, 'alerta');
    }
    return mapa;
  }, [visiveis]);

  // ── Arrastar o 🏁 para outro dia (24/09/2026) ──────────────────────────
  // HTML5 nativo, sem biblioteca (o package.json é travado e não tem dnd). A
  // variante é a do KanbanAtividades — o id viaja no `dataTransfer`, não só
  // num estado que um re-render pode perder — reforçada com o que faltava
  // lá: `effectAllowed`, `onDragEnd` limpando tudo (soltar fora da grade ou
  // Esc), e realce do dia que NÃO pisca: `dragleave` dispara ao passar por
  // cima de cada filho, então só se apaga o realce quando o cursor sai do
  // calendário ou entra em outra célula (ver `alvoDeSoltar`).
  //
  // Quem pode arrastar: `podeArrastarPrazo` (api.ts) — só o 🏁 de negócio e
  // de lembrete, e só com radar.meu.gerenciar. O 🏁 do edital e o ⏰ ficam
  // fixos, com o motivo no tooltip.
  const MIME_ITEM = 'text/x-radar-item';

  const moverParaDia = async (item: ItemRadar, dia: Dayjs) => {
    const ymd = dia.format('YYYY-MM-DD');
    try {
      const novo = await radarApi.atualizar(item.id, { mover_para_dia: ymd });
      // Troca SÓ este item pelo que o servidor devolveu — sem recarregar a
      // lista inteira (a grade não pisca e o mês não muda). O merge preserva
      // qualquer campo que a resposta não traga.
      if (novo && novo.id === item.id) {
        setItens(prev => prev.map(x => (x.id === item.id ? { ...x, ...novo } : x)));
        const atualizado = { ...item, ...novo };
        const aviso = atualizado.prazo && new Date(atualizado.prazo).getTime() < Date.now()
          ? 'data no passado, o aviso não sai'
          : estadoDoAviso(atualizado).texto;
        message.success(`Prazo movido para ${dayjs(novo.prazo || ymd).format('DD/MM [às] HH:mm')} — ${aviso}.`);
      } else {
        message.success(`Prazo movido para ${dia.format('DD/MM')}.`);
        carregar();
      }
    } catch (e: any) {
      message.error(erroDaApi(e, 'Falha ao mover o prazo.'));
    }
  };

  const soltarNoDia = (dia: Dayjs, e: React.DragEvent) => {
    e.preventDefault();
    setDiaAlvo(null);
    setArrastando(null);
    const id = Number(e.dataTransfer.getData(MIME_ITEM));
    const item = itens.find(i => i.id === id);
    if (!item || !podeArrastarPrazo(item, podeGerenciar)) return;
    // Soltou no mesmo dia: nada a fazer (e nada a rearmar no servidor).
    if (item.prazo && dayjs(item.prazo).isSame(dia, 'day')) return;
    // A hora é a que o item já tem (o servidor faz o mesmo: dia novo + hora
    // gravada, 18h se não houver). Serve só para avisar ANTES de mover.
    const base = item.prazo_manual ? dayjs(item.prazo_manual) : null;
    const resultado = dia.hour(base ? base.hour() : 18).minute(base ? base.minute() : 0).second(0);
    if (resultado.isBefore(dayjs())) {
      modal.confirm({
        title: 'Mover para uma data que já passou?',
        content: (
          <Text style={{ fontSize: 13 }}>
            O prazo ficaria em <strong>{resultado.format('DD/MM/YYYY [às] HH:mm')}</strong>, que já
            passou — <strong>o aviso por e-mail não sai</strong>.
          </Text>
        ),
        okText: 'Mover assim mesmo',
        cancelText: 'Cancelar',
        onOk: () => moverParaDia(item, dia),
      });
      return;
    }
    moverParaDia(item, dia);
  };

  // 🚨 A área de soltar é a CÉLULA INTEIRA do dia (25/09/2026). O antd 6 põe
  // o retorno do cellRender só dentro de `.ant-picker-calendar-date-content`
  // (a faixa de conteúdo, 86 px): o número do dia, a borda e o padding ficam
  // fora dele, e soltar em cima do "15" — onde se mira — não fazia nada. Por
  // isso os handlers de arrastar moram num contêiner em volta do <Calendar>
  // (`alvoDeSoltar`), que acha o dia subindo até o <td> e lendo o `data-ymd`
  // deste wrapper; aqui fica só a marca do dia e o realce. O dia vem do
  // `data-ymd` que nós gravamos, e não do `title` do <td>, que depende do
  // locale e do markup interno do antd.
  //
  // Assinatura (date, info) do antd v6. Só a grade de DIAS
  // (`info.type === 'date'`) ganha `data-ymd` — na vista de ano (célula = mês)
  // não há marcação nem alvo de soltar.
  const cellRender = (date: Dayjs, info: { type: string }) => {
    if (info.type !== 'date') return <div style={{ height: '100%' }} />;
    const ymd = date.format('YYYY-MM-DD');
    const lista = porDia.get(ymd) || [];
    const realce = arrastando !== null && diaAlvo === ymd;
    return (
      <div
        data-ymd={ymd}
        style={{
          height: '100%', borderRadius: 4,
          outline: realce ? '2px dashed #020D7F' : 'none', outlineOffset: -2,
          background: realce ? '#EEF1FF' : undefined,
        }}
      >
        {lista.length > 0 && (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, maxHeight: 76, overflow: 'hidden' }}>
            {lista.slice(0, 3).map(({ item, tipo }) => {
              const prazo = classificarPrazoRadar(item);
              const cor = tipo === 'prazo' ? (prazo?.cor || '#444') : '#B07500';
              const marca = marcaDoItem(item);
              const arrastavel = tipo === 'prazo' && podeArrastarPrazo(item, podeGerenciar);
              const dica = tipo === 'alerta'
                ? `Aviso por e-mail às 8h (${item.dias_antecedencia}d antes) · ${item.rotulo} — o dia do aviso acompanha o prazo`
                : item.tipo === 'licitacao'
                  ? `Prazo · ${item.rotulo} — prazo oficial do edital — corrija em Editar`
                  : arrastavel
                    ? `Prazo · ${item.rotulo} — arraste para outro dia (ou Enter para editar)`
                    : `Prazo · ${item.rotulo}`;
              return (
                <li key={`${item.id}-${tipo}`} style={{ marginBottom: 2 }}>
                  <Tooltip title={dica}>
                    <div
                      role="button"
                      tabIndex={0}
                      aria-label={`${tipo === 'prazo' ? 'Prazo' : 'Aviso'} de ${item.rotulo}. Enter abre para editar.`}
                      draggable={arrastavel}
                      onDragStart={arrastavel ? (e) => {
                        e.dataTransfer.setData(MIME_ITEM, String(item.id));
                        e.dataTransfer.effectAllowed = 'move';
                        setArrastando(item.id);
                      } : undefined}
                      onDragEnd={arrastavel ? () => { setArrastando(null); setDiaAlvo(null); } : undefined}
                      onClick={(e) => { e.stopPropagation(); setEditando(item); }}
                      // Teclado e toque: Enter/Espaço abre o ModalEditar, que é
                      // onde se muda a data sem arrastar.
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault(); e.stopPropagation(); setEditando(item);
                        }
                      }}
                      style={{
                        cursor: arrastavel ? 'grab' : 'pointer', fontSize: 10, lineHeight: '14px', color: cor,
                        fontWeight: tipo === 'prazo' ? 600 : 400,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        padding: '0 4px', borderRadius: 2,
                        background: tipo === 'prazo' ? '#FFF5F5' : '#FFFBE8',
                        opacity: arrastando === item.id ? 0.5 : 1,
                      }}
                    >
                      {tipo === 'prazo' ? '🏁' : '⏰'} {marca.emoji} {item.rotulo}
                    </div>
                  </Tooltip>
                </li>
              );
            })}
            {lista.length > 3 && <li style={{ fontSize: 9, color: '#888' }}>+{lista.length - 3} mais</li>}
          </ul>
        )}
      </div>
    );
  };

  // O dia sob o cursor, pela célula INTEIRA: sobe até o <td> da grade e lê o
  // `data-ymd` do wrapper do cellRender. null fora de uma célula de dia
  // (cabeçalho, vista de ano).
  const diaSobOCursor = (alvo: EventTarget | null): string | null => {
    if (!(alvo instanceof Element)) return null;
    const td = alvo.closest('td.ant-picker-cell');
    return td?.querySelector<HTMLElement>('[data-ymd]')?.dataset.ymd || null;
  };
  const alvoDeSoltar = {
    onDragOver: (e: React.DragEvent<HTMLDivElement>) => {
      if (arrastando === null || !e.dataTransfer.types.includes(MIME_ITEM)) return;
      const ymd = diaSobOCursor(e.target);
      // Fora de uma célula de dia (linha dos dias da semana, seletores de mês
      // e ano) não se solta nada — e o realce do último dia tem que apagar,
      // senão ele fica aceso com o cursor mostrando "proibido".
      if (!ymd) { if (diaAlvo !== null) setDiaAlvo(null); return; }
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (diaAlvo !== ymd) setDiaAlvo(ymd);
    },
    // `dragleave` dispara ao passar por cima de cada filho: só apaga o realce
    // quando o cursor sai do contêiner ou cai em OUTRO dia (o dragover de lá
    // acende o novo).
    onDragLeave: (e: React.DragEvent<HTMLDivElement>) => {
      const para = e.relatedTarget;
      if (para instanceof Element && e.currentTarget.contains(para)) {
        const tdPara = para.closest('td.ant-picker-cell');
        const tdDe = e.target instanceof Element ? e.target.closest('td.ant-picker-cell') : null;
        if (!tdPara || tdPara === tdDe) return;
      }
      setDiaAlvo(null);
    },
    onDrop: (e: React.DragEvent<HTMLDivElement>) => {
      const ymd = diaSobOCursor(e.target);
      if (!ymd) { setDiaAlvo(null); setArrastando(null); return; }
      soltarNoDia(dayjs(ymd), e);
    },
  };

  // KPIs pela MESMA régua por dia dos cartões (25/09/2026): antes era uma
  // janela de 168 h, e o +7 recém-criado às 18:00 só entrava no "vencendo em
  // 7 dias" depois das 18:00.
  const agora = Date.now();
  const classificados = visiveis.map(i => classificarPrazoRadar(i, agora))
    .filter((c): c is PrazoRadar => c !== null);
  const vencendo7 = classificados.filter(c => !c.vencido && c.dias >= 0 && c.dias <= 7).length;
  const pendentes = visiveis.filter(i => i.aviso_pendente).length;
  const encerrados = classificados.filter(c => c.vencido).length;

  const agenda = useMemo(
    () => [...visiveis].sort((a, b) => {
      const da = a.prazo ? new Date(a.prazo).getTime() : Number.POSITIVE_INFINITY;
      const db = b.prazo ? new Date(b.prazo).getTime() : Number.POSITIVE_INFINITY;
      return da - db;
    }),
    [visiveis]
  );

  const contar = (t: TipoItem) => itens.filter(i => i.tipo === t).length;

  return (
    <div style={{ width: '100%', margin: '16px 0', padding: '0 24px', boxSizing: 'border-box' }}>
      <Flex justify="space-between" align="flex-start" wrap="wrap" gap={12}>
        <div>
          <Title level={4} style={{ margin: 0, color: '#020D7F' }}>📡 Meu Radar</Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            O que você escolheu acompanhar, com aviso por e-mail antes do prazo. É pessoal:
            só você vê e mexe no seu.
          </Text>
        </div>
        <Space wrap>
          {!mobile && (
            <Segmented
              value={vista}
              onChange={(v) => setVista(v as 'calendario' | 'lista')}
              options={[
                { label: 'Calendário', value: 'calendario', icon: <CalendarOutlined /> },
                { label: 'Agenda', value: 'lista', icon: <UnorderedListOutlined /> },
              ]}
            />
          )}
          <Se permissao="radar.meu.gerenciar">
            <Dropdown
              menu={{
                items: [
                  { key: 'livre', label: '📌  Lembrete (CREA, reunião, contato…)' },
                  ...(podeVerPipeline ? [{ key: 'negocio', label: '💼  Negócio do pipeline' }] : []),
                  ...(podeVerOportunidades ? [{ type: 'divider' as const }, {
                    key: 'licitacao',
                    label: <Link to="/comercial/licitacoes">🏛️  Licitação — pela aba Oportunidades</Link>,
                  }] : []),
                ],
                onClick: ({ key }) => {
                  if (key === 'livre' || key === 'negocio') setCriando(key);
                },
              }}
            >
              <Button type="primary" icon={<PlusOutlined />}>Adicionar</Button>
            </Dropdown>
          </Se>
          <Se permissao="radar.meu.gerenciar">
            <Tooltip title="Mostra quais avisos sairiam agora e só envia depois da sua confirmação.">
              <Button icon={<ExperimentOutlined />} loading={verificando} onClick={conferirAvisos}>
                Conferir avisos
              </Button>
            </Tooltip>
          </Se>
          <Button icon={<ReloadOutlined />} onClick={carregar} loading={carregando}>Atualizar</Button>
        </Space>
      </Flex>

      <Card size="small" style={{ margin: '14px 0', background: '#F7F9FF', border: '1px solid #D7DEF5' }}
        styles={{ body: { padding: 10 } }}>
        <Flex gap={18} wrap="wrap" align="center">
          <Segmented
            size="small"
            value={filtro}
            onChange={(v) => setFiltro(v as Filtro)}
            options={[
              { label: `Tudo (${itens.length})`, value: 'todos' },
              { label: `🏛️ Licitações (${contar('licitacao')})`, value: 'licitacao' },
              { label: `💼 Negócios (${contar('negocio')})`, value: 'negocio' },
              { label: `📌 Lembretes (${contar('livre')})`, value: 'livre' },
            ]}
          />
          <Text style={{ fontSize: 12, color: vencendo7 ? '#B91626' : undefined }}>
            <strong>{vencendo7}</strong> vencendo em 7 dias
          </Text>
          <Text style={{ fontSize: 12, color: pendentes ? '#B07500' : undefined }}>
            <strong>{pendentes}</strong> com aviso a sair
          </Text>
          {encerrados > 0 && (
            <Text type="secondary" style={{ fontSize: 12 }}>{encerrados} com prazo encerrado</Text>
          )}
          <Space size={6}>
            <Tooltip title="Arraste o 🏁 de negócio ou de lembrete para outro dia para mudar o prazo. O do edital segue a fonte — corrija em Editar.">
              <span><Badge color="#B91626" /> <Text style={{ fontSize: 10 }}>🏁 prazo</Text></span>
            </Tooltip>
            <Tooltip title="O aviso por e-mail sai às 8h deste dia. O dia acompanha o prazo e a antecedência — não se arrasta.">
              <span><Badge color="#B07500" /> <Text style={{ fontSize: 10 }}>⏰ aviso às 8h do dia</Text></span>
            </Tooltip>
          </Space>
        </Flex>
      </Card>

      {/* Spinner de página inteira só na PRIMEIRA carga. Nas seguintes a grade
          fica montada (o mês visível e o realce não se perdem) e o Spin cobre
          por cima. */}
      {carregando && !jaCarregou ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
      ) : !visiveis.length ? (
        <Empty
          description={
            <span>
              {itens.length ? 'Nada neste filtro.' : 'Seu radar está vazio.'}<br />
              <Text type="secondary" style={{ fontSize: 12 }}>
                Use <strong>Adicionar</strong> para um lembrete ou um negócio do pipeline, ou o
                botão 📡 na aba <strong>Oportunidades</strong> para um edital.
              </Text>
            </span>
          }
        />
      ) : (
        <Spin spinning={carregando} delay={200}>
          {!mobile && vista === 'calendario' && (
            <Card size="small" styles={{ body: { padding: 8 } }} style={{ marginBottom: 14 }}>
              {/* A área de soltar é este contêiner: o dia sai da célula inteira
                  (ver `alvoDeSoltar`), inclusive de cima do número do dia. */}
              <div {...alvoDeSoltar}>
                <Calendar
                  value={mesVisivel}
                  onChange={setMesVisivel}
                  onPanelChange={(d) => setMesVisivel(d)}
                  cellRender={cellRender as any}
                />
              </div>
            </Card>
          )}
          {/* A agenda fica SEMPRE visível: a grade do mês responde "quando", e
              é a agenda que responde "o quê", com anotação e análise à mão. */}
          <div>
            {!mobile && vista === 'calendario' && (
              <Text type="secondary" style={{ fontSize: 12, display: 'block', margin: '4px 0 8px' }}>
                Agenda — o que vence primeiro
              </Text>
            )}
            {agenda.map(i => (
              <CartaoItem key={i.id} item={i} podeGerenciar={podeGerenciar}
                onEditar={setEditando} onRemover={remover} />
            ))}
          </div>
        </Spin>
      )}

      <ModalNovo tipo={criando} aberto={!!criando}
        onFechar={() => setCriando(null)} onCriado={carregar} />
      <ModalEditar item={editando} aberto={!!editando}
        onFechar={() => setEditando(null)} onSalvo={carregar} />
    </div>
  );
}

function ListaPrevia({ previa }: { previa: ResultadoAlertas }) {
  return (
    <div style={{ maxHeight: '50vh', overflowY: 'auto', marginTop: 8 }}>
      {previa.itens.map(i => (
        <div key={i.id} style={{ fontSize: 12, marginBottom: 8, paddingBottom: 6, borderBottom: '1px solid #eee' }}>
          <div><strong>{i.assunto}</strong></div>
          <div style={{ color: '#666' }}>
            para {i.destinatarios.join(', ')} · faltam {i.dias_restantes} dia(s)
          </div>
        </div>
      ))}
    </div>
  );
}
